import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { loanService, type LoanRequestRow, type ActiveLoanRow } from '../../services/loanService';
import { groupService } from '../../services/groupService';
import { isSuperAdmin } from '../../utils/roles';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import LoanRequestCard from './components/LoanRequestCard';
import LoanActiveCard from './components/LoanActiveCard';
import type { LoanFormData } from './types';

type AdminTab = 'pending' | 'active' | 'my-loans' | 'my-requests' | 'history';
type MemberTab = 'my-loans' | 'my-requests' | 'history';

const LoanManagement = () => {
  const { user, userProfile, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<Array<{ id: string; name: string; currency: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [currency, setCurrency] = useState('VT');
  const [isLoanAdmin, setIsLoanAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('my-loans');

  const [pendingRequests, setPendingRequests] = useState<LoanRequestRow[]>([]);
  const [activeLoans, setActiveLoans] = useState<ActiveLoanRow[]>([]);
  const [memberLoans, setMemberLoans] = useState<ActiveLoanRow[]>([]);
  const [memberRequests, setMemberRequests] = useState<LoanRequestRow[]>([]);
  const [loanHistory, setLoanHistory] = useState<ActiveLoanRow[]>([]);
  const [requestHistory, setRequestHistory] = useState<LoanRequestRow[]>([]);

  const isSuperAdminUser = isSuperAdmin(userProfile?.role);

  const loadGroups = useCallback(async () => {
    if (!user) return;

    let userGroups = await groupService.getUserGroups(user.id);

    if (isSuperAdminUser) {
      const { data: allGroups } = await supabase
        .from('groups')
        .select('id, name, currency')
        .eq('is_active', true)
        .order('name');

      if (allGroups?.length) {
        const merged = new Map(userGroups.map((g) => [g.id, g]));
        allGroups.forEach((g: any) => {
          if (!merged.has(g.id)) {
            merged.set(g.id, {
              id: g.id,
              name: g.name,
              currency: g.currency || 'VT',
            });
          }
        });
        userGroups = Array.from(merged.values());
      }
    }

    setGroups(userGroups);
    if (userGroups.length > 0 && !selectedGroupId) {
      setSelectedGroupId(userGroups[0].id);
      setCurrency(userGroups[0].currency);
    }
  }, [user, isSuperAdminUser, selectedGroupId]);

  const loadData = useCallback(async () => {
    if (!user || !selectedGroupId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const admin = await groupService.isGroupAdminForLoan(selectedGroupId, user.id);
      setIsLoanAdmin(admin);

      const group = groups.find((g) => g.id === selectedGroupId);
      if (group) setCurrency(group.currency);

      if (admin) {
        const [pending, active, allLoans, allRequests] = await Promise.all([
          loanService.getLoanRequests(selectedGroupId, 'pending'),
          loanService.getActiveLoans(selectedGroupId),
          loanService.getAllLoans(selectedGroupId),
          loanService.getLoanRequests(selectedGroupId),
        ]);
        setPendingRequests(pending);
        setActiveLoans(active);
        setLoanHistory(allLoans);
        setRequestHistory(allRequests);
      }

      const [myLoans, myRequests] = await Promise.all([
        loanService.getMemberLoans(user.id),
        loanService.getMemberLoanRequests(user.id),
      ]);

      setMemberLoans(myLoans);
      setMemberRequests(myRequests);
    } catch (err) {
      console.error('Error loading loan data:', err);
    } finally {
      setLoading(false);
    }
  }, [user, selectedGroupId, groups]);

  useEffect(() => {
    setActiveTab(isLoanAdmin ? 'pending' : 'my-loans');
  }, [isLoanAdmin, selectedGroupId]);

  useEffect(() => {
    if (!user) return;
    loadGroups();
  }, [user, loadGroups]);

  useEffect(() => {
    if (selectedGroupId) loadData();
  }, [selectedGroupId, loadData]);

  const handleApprove = async (requestId: string) => {
    if (!user) return;
    const result = await loanService.approveLoanRequest(requestId, user.id);
    if (!result.success) {
      alert(result.error || 'Failed to approve loan');
      return;
    }
    await loadData();
  };

  const handleReject = async (requestId: string, notes: string) => {
    if (!user) return;
    const result = await loanService.rejectLoanRequest(requestId, user.id, notes);
    if (!result.success) {
      alert(result.error || 'Failed to reject loan');
      return;
    }
    await loadData();
  };

  const handleRecordPayment = async (loanId: string, data: LoanFormData) => {
    if (!user) return;
    const result = await loanService.recordLoanPayment({
      loanId,
      amountPaid: parseFloat(data.amount),
      recordedBy: user.id,
      paymentNotes: data.reference || undefined,
    });
    if (!result.success) {
      alert(result.error || 'Failed to record payment');
      return;
    }
    await loadData();
  };

  const adminTabs: { id: AdminTab; label: string }[] = [
    { id: 'pending', label: 'Pending' },
    { id: 'active', label: 'Active' },
    { id: 'my-loans', label: 'My Loans' },
    { id: 'my-requests', label: 'My Requests' },
    { id: 'history', label: 'History' },
  ];

  const memberTabs: { id: MemberTab; label: string }[] = [
    { id: 'my-loans', label: 'My Loans' },
    { id: 'my-requests', label: 'My Requests' },
    { id: 'history', label: 'History' },
  ];

  const tabs = isLoanAdmin ? adminTabs : memberTabs;

  const filteredMemberLoans = memberLoans.filter(
    (l) => !selectedGroupId || l.group_id === selectedGroupId
  );
  const filteredMemberRequests = memberRequests.filter(
    (r) => !selectedGroupId || r.group_id === selectedGroupId
  );

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="ml-3 text-muted-foreground">Loading loans...</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'pending':
        return pendingRequests.length === 0 ? (
          <EmptyState message="No pending loan requests" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingRequests.map((req) => (
              <LoanRequestCard
                key={req.id}
                request={req}
                currency={currency}
                canApprove={isLoanAdmin}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </div>
        );

      case 'active':
        return activeLoans.length === 0 ? (
          <EmptyState message="No active loans" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeLoans.map((loan) => (
              <LoanActiveCard
                key={loan.id}
                loan={loan}
                currency={currency}
                canRecordPayment={isLoanAdmin}
                onRecordPayment={handleRecordPayment}
              />
            ))}
          </div>
        );

      case 'my-loans':
        return filteredMemberLoans.length === 0 ? (
          <EmptyState message="You have no loans" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredMemberLoans.map((loan) => (
              <LoanActiveCard
                key={loan.id}
                loan={loan}
                currency={currency}
                canRecordPayment={false}
                onRecordPayment={handleRecordPayment}
              />
            ))}
          </div>
        );

      case 'my-requests':
        return filteredMemberRequests.length === 0 ? (
          <EmptyState message="You have no loan requests" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredMemberRequests.map((req) => (
              <LoanRequestCard
                key={req.id}
                request={req}
                currency={currency}
                canApprove={false}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </div>
        );

      case 'history':
        return (
          <div className="space-y-6">
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3">Loan history</h3>
              {loanHistory.length === 0 ? (
                <EmptyState message="No loan history for this group" />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {loanHistory.map((loan) => (
                    <LoanActiveCard
                      key={loan.id}
                      loan={loan}
                      currency={currency}
                      canRecordPayment={false}
                      onRecordPayment={handleRecordPayment}
                    />
                  ))}
                </div>
              )}
            </section>
            {isLoanAdmin && (
              <section>
                <h3 className="text-sm font-semibold text-foreground mb-3">Request history</h3>
                {requestHistory.length === 0 ? (
                  <EmptyState message="No request history for this group" />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {requestHistory.map((req) => (
                      <LoanRequestCard
                        key={req.id}
                        request={req}
                        currency={currency}
                        canApprove={false}
                        onApprove={handleApprove}
                        onReject={handleReject}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>Loan Management - communitySaving</title>
        <meta name="description" content="Manage loan requests, approvals, and repayments" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
        />

        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar
              isCollapsed={isSidebarCollapsed}
              userRole={effectiveRole}
            />
          </div>

          <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-4 lg:p-6 max-w-6xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                    Loan Management
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {isLoanAdmin
                      ? 'Approve requests and record repayments for your group'
                      : 'View your loans and request status'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/loan-request')}
                  iconName="Plus"
                >
                  Request Loan
                </Button>
              </div>

              {groups.length === 0 ? (
                <div className="bg-card border border-border rounded-md p-8 text-center">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Join a group to manage loans</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-2">Group</label>
                    <select
                      value={selectedGroupId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedGroupId(id);
                        const g = groups.find((gr) => gr.id === id);
                        if (g) setCurrency(g.currency);
                      }}
                      className="w-full max-w-md px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-2">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-md transition-interactive ${
                          activeTab === tab.id
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {tab.label}
                        {tab.id === 'pending' && pendingRequests.length > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-warning/20 text-warning rounded-full">
                            {pendingRequests.length}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {renderTabContent()}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="bg-card border border-border rounded-md p-8 text-center">
    <Icon name="FileX" size={40} className="text-muted-foreground mx-auto mb-3" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

export default LoanManagement;
